<template>
  <aside
    v-if="wpsClient.media.isPDF || wpsClient.media.isFXL"
    :aria-label="t('zoomSelector.componentTitle')"
    class="zoom-selector h-auto justify-center items-center noselect"
    :class="{
      ['zoom-selector--hidden']: !visibilityStore.showUI,
      ['zoom-selector--interactive-hidden']:
        !visibilityStore.showUI && wpsClient.interactive,
      ['zoom-selector--button']: allowButton,
    }"
  >
    <div class="zoom-selector__box flex justify-center items-center">
      <button
        class="rounded-icon-button rounded-icon-button--left"
        :class="{ ['rounded-icon-button--disabled']: isMin }"
        :title="t('zoomSelector.decreaseZoom')"
        :aria-label="t('zoomSelector.decreaseZoom')"
        :disabled="isMin"
        @click.prevent="decrease"
      >
        <span class="material-icons side-arrow__icon" aria-hidden="true">
          remove
        </span>
      </button>
      <button
        class="zoom-selector__text"
        :class="{ ['zoom-selector__text--horizontal']: verticalMode }"
        :title="t('zoomSelector.selectZoom')"
        :aria-label="
          t('zoomSelector.selectZoom') + ' (' + defineZoomValue() + ')'
        "
        @click="handleToggleCard"
      >
        {{ defineZoomValue() }}
      </button>

      <button
        class="rounded-icon-button rounded-icon-button--right"
        :class="{ ['rounded-icon-button--disabled']: isMax }"
        :title="t('zoomSelector.increaseZoom')"
        :aria-label="t('zoomSelector.increaseZoom')"
        :disabled="isMax"
        @click.prevent="increase"
      >
        <span class="material-icons side-arrow__icon" aria-hidden="true">
          add
        </span>
      </button>
    </div>
  </aside>
  <aside v-if="allowButton" class="zoom-selector__button-zoom">
    <AccessibleButton
      class="square-icon-button"
      :title="t('zoomSelector.selectZoom')"
      :aria-label="t('zoomSelector.selectZoom')"
      @click="handleToggleCard"
    >
      <template #icon>
        <span class="material-icons" aria-hidden="true">zoom_in</span>
      </template>
    </AccessibleButton>
  </aside>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useSettingsStore } from "@/stores/settings";
import { useUiVisibilityStore } from "@/stores/uiVisibility";
import { CustomZoom } from "@/helpers/settings";
import { clamp } from "../../../modules/shared-utils/math";
import { useWpsClient } from "@/wps/client";
import AccessibleButton from "./AccessibleButton.vue";

const { t } = useI18n();

const wpsClient = useWpsClient();

const settingsStore = useSettingsStore();
const visibilityStore = useUiVisibilityStore();

const props = withDefaults(
  defineProps<{
    verticalMode: boolean;
    allowButton?: boolean;
  }>(),
  {
    allowButton: false,
  }
);

const minValue = computed<number>(() => (wpsClient.media.isFXL ? 100 : 50));
const maxValue = computed<number>(() => (wpsClient.media.isFXL ? 600 : 400));

const effectiveNumber = computed<number>(() => {
  // TODO: previous implementation actually took into account the computed "live" value
  let val = parseInt(settingsStore.perContent.customZoomPDF as string);
  if (isNaN(val)) {
    val = 100;
  }
  return val;
});

const isMin = computed<boolean>(() => effectiveNumber.value <= minValue.value);
const isMax = computed<boolean>(() => effectiveNumber.value >= maxValue.value);

function decrease() {
  const value = effectiveNumber.value;
  setNewZoom(wpsClient.media.isPDF ? value - 10 : Math.round(value * 0.9));
}

function increase() {
  const value = effectiveNumber.value;
  setNewZoom(wpsClient.media.isPDF ? value + 10 : Math.round(value * 1.1));
}

function setNewZoom(value: number) {
  value = clamp(value, minValue.value, maxValue.value);

  let storedValue: typeof settingsStore.perContent.customZoomPDF =
    value.toString() as `${number}`;
  if (wpsClient.media.isPDF && value === 100) {
    storedValue = CustomZoom.Fit;
  }

  settingsStore.perContent.customZoomPDF = storedValue;
}

function handleToggleCard(e: Event) {
  e.preventDefault();
  visibilityStore.setShowZoomCard(!visibilityStore.showZoomCard);
}

function defineZoomValue() {
  const customZoom = settingsStore.perContent.customZoomPDF;
  const val = parseInt(customZoom as string);
  if (isNaN(val)) {
    return t("zoomSelector." + customZoom);
  } else if (customZoom === "0") {
    return t(
      "zoomSelector." + (props.verticalMode ? "page-width" : "page-fit")
    );
  } else {
    return Math.round(val) + "%";
  }
}
</script>

<style lang="scss" scoped>
// imported reader instead variables because here is used .square-icon-button
@use "@/assets/styles/reader.scss" as *;

.zoom-selector {
  z-index: 1;
  opacity: 1;
  width: $zoomComponent;
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  border-radius: $spacingM;
  transition: opacity 0.8s cubic-bezier(0, 1, 0.5, 1);

  &--hidden {
    opacity: 0;
  }

  &--interactive-hidden {
    user-select: none;
    pointer-events: none;
    z-index: -1;
  }

  &--button {
    display: none;
  }

  @media (min-width: $screenL) {
    display: block;
    &__button-zoom {
      display: none;
    }
  }

  &__box {
    height: $squareButtonSize;
  }

  &__button-zoom {
    z-index: 3;
    padding-left: $spacingM;
    border-left: 2px solid var(--DM__theme-separator-color);
  }

  &__text {
    display: flex;
    position: relative;
    align-items: center;
    width: $zoomComponent;
    height: $squareButtonSize;
    padding: $spacingS $spacingL $spacingS $spacingS;
    border-top: $spacingXS solid var(--DM__color-grayBase);
    border-bottom: $spacingXS solid var(--DM__color-grayBase);
    font-size: 0.9rem;
    background-color: var(--DM__theme-fill-color);

    &::after {
      content: "expand_more";
      position: absolute;
      width: $spacingL;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      z-index: 0;
      font-size: 1.2rem;
      font-family: "Material Icons";
      font-feature-settings: "liga";
      -webkit-font-feature-settings: "liga";
      transition: all 0.2s;
    }

    @media (any-hover: hover) {
      &:hover,
      &:focus,
      &:active {
        outline: 0;
        background-color: var(--DM__theme-hover-color);
      }

      &:hover {
        border-top: solid $spacingXS var(--DM__color-grayBase);
        border-bottom: solid $spacingXS var(--DM__color-grayBase);
      }

      &:focus {
        border-top: solid $spacingXS var(--DM__color-grayBase);
        border-bottom: solid $spacingXS var(--DM__color-grayBase);
      }

      &:focus:not(:focus-visible) {
        outline: 0;
        box-shadow: none;
        &:hover {
          background-color: var(--DM__theme-hover-color);
        }
      }
    }
  }
}

.rounded-icon-button {
  @media (any-hover: hover) {
    &:focus {
      color: var(--DM__theme-font-color);
      border: solid $spacingXS var(--DM__color-grayBase);
    }

    &:hover,
    &:active {
      border: solid $spacingXS var(--DM__color-grayBase);
      outline: 0;
      background-color: var(--DM__theme-hover-color);
    }

    &:focus:not(:focus-visible) {
      outline: 0;
      box-shadow: none;
      border: solid $spacingXS var(--DM__color-grayBase);
    }

    &--disabled {
      &:hover {
        color: var(--DM__color-grayShadow);
        background-color: var(--DM__color-grisFonce);
        border-color: var(--DM__color-grayShadow);
      }
    }
  }
}
</style>
